import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext();

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [mockData, setMockData] = useState({});

  // Load mock data from JSON files
  useEffect(() => {
    const loadMockData = async () => {
      try {
        // Load all mock data files
        const dataFiles = [
          'superAdminDashboard', 'tenants', 'organizations', 'departments', 'teams', 'teamMembers',
          'systemHealth', 'recentActivity', 'approverData', 'integrationData', 'billingData', 
          'aiOrchestratorData', 'users', 'userRoles', 'tickets', 'dashboard', 'chatSessions', 'feedback', 
          'analytics', 'systemRoles', 'permissions'
        ];

        const loadedData = {};
        for (const file of dataFiles) {
          try {
            const response = await fetch(`/data/mock/${file}.json`);
            if (response.ok) {
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                loadedData[file] = await response.json();
              } else {
                // Response is not JSON (likely HTML error page), use default data
                loadedData[file] = getDefaultData(file);
              }
            } else {
              // File doesn't exist (404), use default data silently
              loadedData[file] = getDefaultData(file);
            }
          } catch (error) {
            // Silently use default data if file doesn't exist or network error
            // Only log if it's not a 404 or network error
            if (error.name !== 'TypeError' && !error.message.includes('JSON')) {
              console.warn(`Failed to load ${file}.json:`, error);
            }
            loadedData[file] = getDefaultData(file);
          }
        }

        setMockData(loadedData);
      } catch (error) {
        console.error('Error loading mock data:', error);
        // Fallback to default data
        setMockData(getDefaultMockData());
      }
    };

    loadMockData();
  }, []);

  const getMockData = (key) => {
    return mockData[key] || {};
  };

  const updateMockData = (key, data) => {
    setMockData(prev => ({
      ...prev,
      [key]: data
    }));
    
    // Persist to localStorage for session persistence
    try {
      localStorage.setItem(`mockData_${key}`, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  };

  const value = {
    getMockData,
    updateMockData,
    mockData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

// Default data fallbacks
const getDefaultData = (key) => {
  const defaults = {
    superAdminDashboard: {
      metrics: {
        totalTenants: 45,
        activeTenants: 42,
        suspendedTenants: 3,
        pendingTenants: 0,
        totalUsers: 1250,
        systemHealth: "99.9%",
        systemAlerts: 2
      },
      tenants: [
        {
          id: 1,
          name: "Acme Corp",
          domain: "acme.example.com",
          status: "active",
          userCount: 150,
          lastActivity: "2025-01-15T10:30:00Z",
          plan: "enterprise",
          createdAt: "2024-01-15T10:30:00Z"
        }
      ],
      systemHealth: {
        uptime: "99.9%",
        responseTime: "120ms",
        trends: [
          { name: "Jan 1", value: 99.5 },
          { name: "Jan 2", value: 99.7 },
          { name: "Jan 3", value: 99.9 },
          { name: "Jan 4", value: 99.8 },
          { name: "Jan 5", value: 99.9 }
        ]
      },
      recentActivity: [
        {
          type: "success",
          message: "Tenant 'Acme Corp' upgraded to Enterprise plan",
          timestamp: "2025-01-15T10:30:00Z"
        },
        {
          type: "warning",
          message: "High CPU usage detected on server-02",
          timestamp: "2025-01-15T09:45:00Z"
        },
        {
          type: "info",
          message: "New tenant 'StartupXYZ' registered",
          timestamp: "2025-01-15T08:20:00Z"
        }
      ]
    },
    tenants: {
      tenants: [
        {
          id: 1,
          name: "Acme Corp",
          domain: "acme.example.com",
          status: "active",
          userCount: 150,
          lastActivity: "2025-01-15T10:30:00Z",
          plan: "enterprise",
          createdAt: "2024-01-15T10:30:00Z",
          adminEmail: "admin@acme.com",
          description: "Enterprise customer with high-volume support needs"
        },
        {
          id: 2,
          name: "TechStart Inc",
          domain: "techstart.example.com",
          status: "active",
          userCount: 75,
          lastActivity: "2025-01-15T09:15:00Z",
          plan: "professional",
          createdAt: "2024-06-15T10:30:00Z",
          adminEmail: "admin@techstart.com",
          description: "Growing startup with technical support needs"
        }
      ]
    },
    systemHealth: {
      health: {
        uptime: "99.9%",
        responseTime: "120ms",
        cpuUsage: "45%",
        memoryUsage: "67%",
        diskUsage: "23%",
        trends: [
          { name: "Jan 1", value: 99.5 },
          { name: "Jan 2", value: 99.7 },
          { name: "Jan 3", value: 99.9 },
          { name: "Jan 4", value: 99.8 },
          { name: "Jan 5", value: 99.9 }
        ]
      }
    },
    recentActivity: {
      activities: [
        {
          type: "success",
          message: "Tenant 'Acme Corp' upgraded to Enterprise plan",
          timestamp: "2025-01-15T10:30:00Z"
        },
        {
          type: "warning",
          message: "High CPU usage detected on server-02",
          timestamp: "2025-01-15T09:45:00Z"
        },
        {
          type: "info",
          message: "New tenant 'StartupXYZ' registered",
          timestamp: "2025-01-15T08:20:00Z"
        }
      ]
    },
    approverData: { approvals: [], metrics: {} },
    integrationData: { integrations: [], metrics: {} },
    billingData: { subscriptions: [], metrics: {} },
    aiOrchestratorData: { models: [], metrics: {} },
    users: { users: [] },
    organizations: {
      organizations: [
        {
          id: 1,
          tenantId: 1,
          name: 'Acme Retail',
          code: 'ACME_RETAIL',
          description: 'Retail division of Acme Corp',
          isActive: true,
          tenantName: 'Acme Corp',
          departmentCount: 5,
          userCount: 45,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: 2,
          tenantId: 1,
          name: 'Acme Manufacturing',
          code: 'ACME_MFG',
          description: 'Manufacturing division of Acme Corp',
          isActive: true,
          tenantName: 'Acme Corp',
          departmentCount: 3,
          userCount: 60,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: 3,
          tenantId: 1,
          name: 'Acme Services',
          code: 'ACME_SVC',
          description: 'Services division of Acme Corp',
          isActive: true,
          tenantName: 'Acme Corp',
          departmentCount: 4,
          userCount: 35,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        }
      ]
    },
    departments: {
      departments: [
        {
          id: 1,
          tenantId: 1,
          organizationId: 1,
          name: 'HR',
          code: 'HR',
          description: 'Human Resources Department',
          isActive: true,
          organizationName: 'Acme Retail',
          teamCount: 2,
          userCount: 12,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: 2,
          tenantId: 1,
          organizationId: 1,
          name: 'IT',
          code: 'IT',
          description: 'Information Technology Department',
          isActive: true,
          organizationName: 'Acme Retail',
          teamCount: 3,
          userCount: 18,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: 3,
          tenantId: 1,
          organizationId: 1,
          parentDepartmentId: 2,
          name: 'IT Support',
          code: 'IT_SUPPORT',
          description: 'IT Support sub-department',
          isActive: true,
          organizationName: 'Acme Retail',
          teamCount: 2,
          userCount: 10,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: 4,
          tenantId: 1,
          organizationId: 1,
          parentDepartmentId: 2,
          name: 'IT Development',
          code: 'IT_DEV',
          description: 'IT Development sub-department',
          isActive: true,
          organizationName: 'Acme Retail',
          teamCount: 1,
          userCount: 8,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: 5,
          tenantId: 1,
          organizationId: 1,
          name: 'Finance',
          code: 'FIN',
          description: 'Finance Department',
          isActive: true,
          organizationName: 'Acme Retail',
          teamCount: 1,
          userCount: 15,
          status: 'active',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        }
      ]
    },
    teams: { teams: [] },
    teamMembers: { members: [] },
    userRoles: { userRoles: [] },
    systemRoles: { roles: [] },
    permissions: { permissions: [] },
    tickets: { tickets: [] },
    dashboard: { metrics: {} },
    chatSessions: { sessions: [] },
    feedback: { feedback: [] },
    analytics: { analytics: [] }
  };
  return defaults[key] || {};
};

const getDefaultMockData = () => ({
  superAdminDashboard: getDefaultData('superAdminDashboard'),
  tenants: getDefaultData('tenants'),
  organizations: getDefaultData('organizations'),
  departments: getDefaultData('departments'),
  teams: getDefaultData('teams'),
  teamMembers: getDefaultData('teamMembers'),
  userRoles: getDefaultData('userRoles'),
  systemRoles: getDefaultData('systemRoles'),
  permissions: getDefaultData('permissions'),
  systemHealth: getDefaultData('systemHealth'),
  recentActivity: getDefaultData('recentActivity'),
  approverData: getDefaultData('approverData'),
  integrationData: getDefaultData('integrationData'),
  billingData: getDefaultData('billingData'),
  aiOrchestratorData: getDefaultData('aiOrchestratorData'),
  users: getDefaultData('users'),
  tickets: getDefaultData('tickets'),
  dashboard: getDefaultData('dashboard'),
  chatSessions: getDefaultData('chatSessions'),
  feedback: getDefaultData('feedback'),
  analytics: getDefaultData('analytics')
});
